import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { ConfigContext } from "../Config/ConfigContext";
import {
	Container,
	CircularProgress,
	Grid,
	Card,
	CardContent,
	CardActions,
	Button,
	Typography,
	Link as MuiLink
} from "@mui/material";
import Box from "@mui/material/Box";
import { NavbarTop } from "../NavbarTop";
import useApi from "../hooks/useApi";

/**
 * Paywall component
 *
 * Responsibilities:
 * - Determine if the current user has an active paid subscription by calling payment.getSubscriptionStatus().
 * - If the user is NOT paid, fetch a list of available Stripe Payment Links via payment.getLinks() and render them.
 * - If the user IS paid, render the children passed to this component (the protected experience).
 *
 * Rendering for non-subscribed users:
 * - Displays available Stripe Payment Links as cards, including:
 *   - A plan name (link.name) when provided; falls back to a label based on signUpType (e.g., "Plan for Organization").
 *   - A plan description (link.description) when provided.
 *   - A large, bold USD price shown directly under the description when present. The component:
 *       - Prefers explicit numeric fields (priceUsd in cents or price in dollars) if available.
 *       - Falls back to parsing the first $-prefixed amount from the description (e.g., "$250 per month").
 *   - A list of "features" when provided by the link document. Each feature is an object with:
 *       { id: string, value: string, description: string }
 *     We render the feature.description when available, otherwise fall back to "id: value" or just "id".
 *   - The direct checkout URL (rendered as a clickable link) and an action button to proceed to checkout.
 *
 * Notes:
 * - The available payment links returned from the API already include the user's client_reference_id as a query param.
 * - Each link document can contain a "features" array of objects; we use feature.description primarily for display.
 *
 * Stability and re-render behavior:
 * - The useEffect below depends only on config?.uri?.base so it runs when the environment changes.
 * - We intentionally DO NOT include "payment" in the dependency array because some hooks return
 *   a non-stable object each render, which would cause this effect to re-run repeatedly and
 *   repeatedly call the payment APIs. This prevents the repeated API calls the user observed.
 * - In React 18 StrictMode (development), effects may run twice due to intentional mount/unmount cycles;
 *   this might still cause a duplicated fetch in development, but not in production.
 *
 * Styling:
 * - The "Proceed to Checkout" button now switches to a white background on hover (with text set to the brand color)
 *   to ensure proper contrast and readability when the text color changes on hover.
 *
 * Layout update (Cards):
 * - Each plan card now has a minimum height of 550px.
 * - The card uses a flex column layout where CardContent expands to fill available space (flexGrow: 1),
 *   ensuring the CardActions section (which contains the "Proceed to Checkout" button) is pushed to the bottom.
 * - Card text content is explicitly left-aligned to avoid inheriting center alignment from the parent container.
 *
 * Layout update (Navbar):
 * - A top navigation bar (NavbarTop) has been added to this component so the logo appears at the top-left.
 * - A spacer (Box with 100px height) is inserted under the AppBar to offset the fixed navbar height and prevent
 *   the content from being overlapped by the AppBar.
 * - The logo and site name are resolved from configuration when available and passed to NavbarTop as props.
 *
 * Centering update (Paywall content and loading state):
 * - The paywall content (when the user is not subscribed) and the loading state are now centered both
 *   vertically and horizontally within the viewport area below the fixed navbar.
 * - This is achieved by giving the main Container a minHeight of (100vh - NAVBAR_HEIGHT) and applying
 *   flex styles to center its children. Grid content is also horizontally centered.
 *
 * Usage:
 * <Paywall>
 *   <YourProtectedApp />
 * </Paywall>
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Children to render once loading is completed and the user has a paid subscription.
 * @returns {JSX.Element} Rendered component.
 */

/**
 * Extract and format a USD price string for display from a payment link object.
 *
 * Strategy:
 * - Prefer explicit numeric fields if present:
 *     - link.priceUsd (number, in cents) -> dollars = priceUsd / 100
 *     - link.price (number, in dollars) -> dollars = price
 * - Fallback:
 *     - Parse the first $-prefixed amount that appears in link.description
 *       e.g., "$250 per month" -> 250
 *
 * Returns:
 * - A string like "$250" or "$250.00" suitable for direct display, or null if no amount could be determined.
 *
 * @param {Object} link - Payment link object.
 * @returns {string|null} - Formatted USD price string, or null when not found.
 */
function getFormattedUsdPrice(link) {
	// 1) Prefer explicit numeric fields
	if (typeof link?.priceUsd === "number" && isFinite(link.priceUsd)) {
		const dollars = link.priceUsd / 100;
		return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(dollars);
	}
	if (typeof link?.price === "number" && isFinite(link.price)) {
		return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(link.price);
	}

	// 2) Fallback: parse from description (first $-amount)
	const desc = link?.description;
	if (typeof desc === "string" && desc.length > 0) {
		const match = desc.match(/\$\s*\d{1,3}(?:,\d{3})*(?:\.\d{2})?/);
		if (match && match[0]) {
			// Normalize to a numeric value then reformat as USD
			const numeric = Number(match[0].replace(/[^0-9.]/g, ""));
			if (!Number.isNaN(numeric)) {
				return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(numeric);
			}
		}
	}

	return null;
}

export default function Paywall(props) {
	const [loading, setLoading] = useState(true);
	const [isPaid, setIsPaid] = useState(false);
	const [paymentLinks, setPaymentLinks] = useState([]);
	const [error, setError] = useState(null);

	// Keep config in dependency list so this re-evaluates if environment changes
	const { config } = React.useContext(ConfigContext);

	// API hooks
	const { payment } = useApi();

	// Fixed navbar height used to offset and center content within the remaining viewport
	const NAVBAR_HEIGHT_PX = 100;

	/**
	 * Resolve common fields safely from config to feed NavbarTop.
	 * Only non-empty strings will be returned; otherwise undefined is used.
	 */
	const resolveFirstString = (...vals) => {
		for (const v of vals) {
			if (typeof v === "string" && v.trim().length > 0) return v;
		}
		return undefined;
	};

	const siteName = props.siteName;

	const logoSrc = props.logo;

	useEffect(() => {
		let isMounted = true;

		const run = async () => {
			setLoading(true);
			setError(null);

			try {
				// 1) Check subscription status
				const status = await payment.getSubscriptionStatus();
				const hasActivePaidSubscription = !!status?.hasActivePaidSubscription;

				if (!isMounted) return;

				if (hasActivePaidSubscription) {
					// User is paid; allow access
					setIsPaid(true);
					setPaymentLinks([]);
					setLoading(false);
					return;
				}

				// 2) Not paid; fetch available payment links
				try {
					const linkResp = await payment.getLinks();
					if (!isMounted) return;
					setPaymentLinks(Array.isArray(linkResp?.paymentLinks) ? linkResp.paymentLinks : []);
				} catch (linkErr) {
					if (!isMounted) return;
					setPaymentLinks([]);
					setError(linkErr?.message || "Unable to retrieve payment links.");
				}

				setIsPaid(false);
				setLoading(false);
			} catch (err) {
				// If we cannot determine status, treat as not paid but show an error
				if (!isMounted) return;
				setIsPaid(false);
				setPaymentLinks([]);
				setError(err?.message || "Unable to determine subscription status.");
				setLoading(false);
			}
		};

		run();

		return () => {
			isMounted = false;
		};
		// IMPORTANT: Do not include "payment" in dependencies to avoid repeated API calls
	}, [config?.uri?.base]);

    if (!loading && isPaid && props.children)
      return (
        props.children
      );

	return (
		<React.Fragment>
			{/* Top Navbar with logo on the left when available */}
			<NavbarTop logo={logoSrc} siteName={siteName} />

			{/* Centered loading state within the viewport area below the navbar */}
			{loading && (
				<Container
					sx={{
						py: 6,
						minHeight: `calc(100vh - ${NAVBAR_HEIGHT_PX}px)`,
						display: "flex",
						alignItems: "center",
						justifyContent: "center"
					}}
				>
					<CircularProgress />
				</Container>
			)}

			{/* Center the paywall content (when not subscribed) both vertically and horizontally */}
			{!loading && !isPaid && (
				<Container
					sx={{
						//py: 6,
						minHeight: `calc(100vh - ${NAVBAR_HEIGHT_PX}px)`,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						textAlign: "center"
					}}
				>
					<Typography variant="h5" gutterBottom>
						Subscription required
					</Typography>
					<Typography variant="body1" color="text.secondary" paragraph>
						To continue, please choose one of the available plans below to complete your purchase.
					</Typography>

					{error && (
						<Typography variant="body2" color="error" paragraph>
							{error}
						</Typography>
					)}

					{paymentLinks.length === 0 && !error && (
						<Typography variant="body2" color="text.secondary">
							No payment links are currently available for your account.
						</Typography>
					)}

					{paymentLinks.length > 0 && (
						<Grid container spacing={2} justifyContent="center">
							{paymentLinks.map((link) => {
								const key = link?._id || link?.link || Math.random().toString(36).slice(2);
								const features = Array.isArray(link?.features) ? link.features : [];

								// Prefer explicit plan name, fall back to signUpType-derived label, then a generic label.
								const planLabel =
									link?.name && String(link.name).trim().length > 0
										? link.name
										: link?.signUpType
											? `Plan for ${link.signUpType.charAt(0).toUpperCase()}${link.signUpType.slice(1)}`
											: "Available Plan";

								const planDescription =
									link?.description && String(link.description).trim().length > 0
										? link.description
										: null;

								// Extract a formatted USD price to display under the description
								const formattedPrice = getFormattedUsdPrice(link);

								return (
									<Grid item xs={12} sm={6} md={4} key={key}>
										<Card
											variant="outlined"
											// Layout: Make each card at least 550px tall and use flex column so actions can sit at the bottom.
											sx={{
												display: "flex",
												flexDirection: "column",
												minHeight: 550,
												textAlign: "left"
											}}
										>
											<CardContent
												// Allow content to expand so that CardActions is pushed to the bottom.
												sx={{ flexGrow: 1, textAlign: "left" }}
											>
												<Typography variant="h6" gutterBottom>
													{planLabel}
												</Typography>

												{planDescription && (
													<Typography variant="body2" color="text.secondary" paragraph>
														{planDescription}
													</Typography>
												)}

												{formattedPrice && (
													<Typography
														variant="h4"
														component="div"
														sx={{ fontWeight: 700, mb: 1 }}
													>
														{formattedPrice}
													</Typography>
												)}

												{/*link?.link && (
													<Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
														Checkout link:{" "}
														<MuiLink
															href={link.link}
															target="_blank"
															rel="noopener noreferrer"
															underline="hover"
															sx={{ wordBreak: "break-all" }}
														>
															{link.link}
														</MuiLink>
													</Typography>
												)*/}

												{features.length > 0 && (
													<React.Fragment>
														<Typography variant="subtitle2" color="text.secondary">
															Includes:
														</Typography>
														<ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
															{features.map((f, idx) => {
																const featureKey = f?.id || `${key}-feature-${idx}`;
																const featureText =
																	(f && typeof f === "object" && (f.description || null)) ||
																	[f?.id, f?.value].filter(Boolean).join(": ") ||
																	String(f);

																return (
																	<li key={featureKey}>
																		<Typography variant="body2">{featureText}</Typography>
																	</li>
																);
															})}
														</ul>
													</React.Fragment>
												)}
											</CardContent>
											<CardActions
												// Ensure actions area stays at the bottom in the flex layout.
												sx={{ mt: "auto" }}
											>
												<Button
													variant="contained"
													color="primary"
													fullWidth
													href={link?.link}
													target="_blank"
													rel="noopener noreferrer"
													// On hover, switch to white background and brand-colored text for better contrast.
													sx={(theme) => ({
														'&:hover': {
															backgroundColor: '#ffffff',
															color: theme.palette.primary.main,
														},
													})}
												>
													Proceed to Checkout
												</Button>
											</CardActions>
										</Card>
									</Grid>
								);
							})}
						</Grid>
					)}
				</Container>
			)}
		</React.Fragment>
	);
}

Paywall.propTypes = {
	children: PropTypes.node.isRequired
};